import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DollarSign, Download, Mail, CheckCircle, FileText, Send, History } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Database } from "@/integrations/supabase/types";

type Invoice = Database["public"]["Tables"]["invoices"]["Row"];

interface InvoiceCardProps {
  invoice: Invoice;
  onSend: (invoiceId: string) => void;
  onMarkPaid: (invoiceId: string) => void;
  onDownloadPDF: (invoiceId: string) => void;
  onSendEmail: (invoiceId: string, customerEmail: string) => void;
  onSendReminder: (invoiceId: string, customerEmail: string) => void;
  onRecordPayment: (invoiceId: string, amount: number, method: string) => void;
  customerEmail?: string;
}

const statusConfig: Record<string, { label: string; className: string }> = {
  draft: { label: "Draft", className: "bg-secondary text-secondary-foreground" },
  sent: { label: "Sent", className: "bg-primary/20 text-primary" },
  paid: { label: "Paid", className: "bg-success/20 text-success" },
  cancelled: { label: "Cancelled", className: "bg-destructive/20 text-destructive" },
};

export const InvoiceCard = ({ 
  invoice, 
  onSend, 
  onMarkPaid, 
  onDownloadPDF,
  onSendEmail,
  onSendReminder,
  onRecordPayment,
  customerEmail 
}: InvoiceCardProps) => {
  const statusInfo = statusConfig[invoice.status] || statusConfig.draft;
  
  const isOverdue = invoice.due_date && new Date(invoice.due_date) < new Date() && invoice.status !== 'paid';
  const daysUntilDue = invoice.due_date 
    ? Math.ceil((new Date(invoice.due_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-xl font-bold">Invoice #{invoice.invoice_number}</h3>
            <Badge className={statusInfo.className}>{statusInfo.label}</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {invoice.issued_date
              ? `Issued: ${format(new Date(invoice.issued_date), "MMM dd, yyyy")}`
              : "Not issued yet"}
          </p>
          {invoice.due_date && (
            <p className="text-sm text-muted-foreground">
              Due: {format(new Date(invoice.due_date), "MMM dd, yyyy")}
            </p>
          )}
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-primary">${invoice.total_amount.toLocaleString()}</p>
          <p className="text-sm text-muted-foreground">{invoice.payment_terms}</p>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="space-y-2 mb-4 pb-4 border-b">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Base Estimate:</span>
          <span className="font-medium">${invoice.base_estimate_cost.toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Materials:</span>
          <span className="font-medium">${invoice.materials_cost.toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Labor:</span>
          <span className="font-medium">${invoice.labor_cost.toLocaleString()}</span>
        </div>
        {invoice.supplements_cost > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Supplements:</span>
            <span className="font-medium">${invoice.supplements_cost.toLocaleString()}</span>
          </div>
        )}
        <div className="flex justify-between text-sm pt-2 border-t">
          <span className="text-muted-foreground">Subtotal:</span>
          <span className="font-medium">${invoice.subtotal.toLocaleString()}</span>
        </div>
        {invoice.tax_amount > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Tax:</span>
            <span className="font-medium">${invoice.tax_amount.toLocaleString()}</span>
          </div>
        )}
      </div>

      {/* Email Status */}
      {invoice.email_sent_to && (
        <div className="mb-4 pb-4 border-b">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Sent to:</span>
            <span className="font-medium">{invoice.email_sent_to}</span>
          </div>
          {invoice.email_opened_at && (
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-muted-foreground">Opened:</span>
              <span className="text-success">{format(new Date(invoice.email_opened_at), "MMM dd, yyyy 'at' h:mm a")}</span>
            </div>
          )}
          {invoice.reminder_count > 0 && (
            <div className="flex items-center justify-between text-sm mt-2">
              <span className="text-muted-foreground">Reminders sent:</span>
              <span className="font-medium">{invoice.reminder_count}</span>
            </div>
          )}
        </div>
      )}

      {/* Overdue Warning */}
      {isOverdue && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive font-medium">
            ⚠️ This invoice is {Math.abs(daysUntilDue || 0)} days overdue
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDownloadPDF(invoice.id)}
        >
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
        
        {invoice.status === "draft" && customerEmail && (
          <>
            <Button
              size="sm"
              onClick={() => onSendEmail(invoice.id, customerEmail)}
            >
              <Mail className="w-4 h-4 mr-2" />
              Send via Email
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSend(invoice.id)}
            >
              Mark as Sent
            </Button>
          </>
        )}
        
        {invoice.status === "sent" && (
          <>
            {customerEmail && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSendReminder(invoice.id, customerEmail)}
              >
                <Send className="w-4 h-4 mr-2" />
                Send Reminder
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => onMarkPaid(invoice.id)}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Mark as Paid
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRecordPayment(invoice.id, 0, "partial")}
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Record Payment
            </Button>
          </>
        )}
      </div>

      {invoice.notes && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm text-muted-foreground">{invoice.notes}</p>
        </div>
      )}

      {invoice.paid_date && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm text-success flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Paid on {format(new Date(invoice.paid_date), "MMM dd, yyyy")}
          </p>
        </div>
      )}
    </Card>
  );
};
